import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Phone, MapPin, Mail } from 'lucide-react';

const Footer = () => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { delayChildren: 0.2, staggerChildren: 0.1 }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: { y: 0, opacity: 1, transition: { duration: 0.6, ease: "easeOut" } }
  };

  const menuLinks = [
    { name: "Home", href: "/" },
    { name: "About", href: "/about" },
    { name: "Events", href: "/all-events" },
    { name: "Impact", href: "/impact" },
    { name: "Partners", href: "/partners" },
    { name: "Annual Sponsors", href: "/annual-sponsors" },
    { name: "Team", href: "/team" },
    { name: "Media", href: "/media" },
    { name: "Contact", href: "/contact" },
  ];

  return (
    <footer className="footer">
      <div className="container">
        <motion.div
          className="footer-content"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          <motion.div variants={itemVariants} className="footer-about">
            <div className="footer-logo">
              <img src="/images/logo.webp" alt="3E Entertainment" loading="lazy" decoding="async" />
            </div>
            <p>
              3E Entertainment is a premier platform for Nepali cultural
              experiences in the United States, creating festivals, artist
              tours, and community events that preserve heritage, unite
              generations, and give back with purpose.
            </p>
            <p className="footer-tagline">Encourage. Engage. Empower.</p>
          </motion.div>

          <motion.div variants={itemVariants}>
            <h3>MENU</h3>
            <ul className="footer-nav">
              {menuLinks.map((link) => (
                <li key={link.name}>
                  <Link to={link.href}>{link.name}</Link>
                </li>
              ))}
            </ul>
          </motion.div>

          <motion.div variants={itemVariants}>
            <h3>CONTACT US</h3>
            <div className="footer-contact-item">
              <MapPin size={15} />
              <p>3609-C Chain Bridge Road<br />Fairfax, VA 22031</p>
            </div>
            <div className="footer-contact-item">
              <Phone size={15} />
              <p><a href="tel:8333833783">833-383-3783</a></p>
            </div>
            <div className="footer-contact-item">
              <Mail size={15} />
              <p><a href="mailto:info@3etainment.com">info@3etainment.com</a></p>
            </div>
            <div className="footer-contact-item">
              <span className="footer-web-icon">🌐</span>
              <p><a href="https://www.3etainment.com" target="_blank" rel="noopener noreferrer">www.3etainment.com</a></p>
            </div>
          </motion.div>
        </motion.div>

        <motion.div
          className="footer-bottom"
          variants={itemVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          <p>Copyright &copy; {new Date().getFullYear()} 3E Entertainment &nbsp;|&nbsp; Encourage. Engage. Empower.</p>
        </motion.div>
      </div>

      <style>{`
        .footer {
          background-color: #0a0a0a;
          color: #fff;
          padding: 5rem 0 2rem;
          position: relative;
        }

        .container {
          max-width: 1200px;
          margin: 0 auto;
          padding: 0 2rem;
        }

        .footer-content {
          display: grid;
          grid-template-columns: 1.4fr 0.8fr 1fr;
          gap: 4rem;
          margin-bottom: 3rem;
        }

        .footer-about .footer-logo img {
          height: 44px;
          width: auto;
          margin-bottom: 1.25rem;
          opacity: 0.9;
        }

        .footer-about p {
          color: #bbb;
          font-size: 0.92rem;
          line-height: 1.8;
          margin-bottom: 1rem;
        }

        .footer-tagline {
          font-weight: 700;
          color: var(--crimson-on-dark, #e8505b) !important;
          font-size: 0.9rem !important;
          letter-spacing: 0.5px;
          margin-top: 1.25rem !important;
        }

        .footer-content h3 {
          color: #fff;
          font-size: 0.8rem;
          font-weight: 700;
          margin-bottom: 1.5rem;
          letter-spacing: 2px;
          text-transform: uppercase;
        }

        .footer-nav {
          list-style: none;
          padding: 0;
          margin: 0;
          display: flex;
          flex-direction: column;
          gap: 0.65rem;
        }

        .footer-nav li a {
          color: #bbb;
          text-decoration: none;
          font-size: 0.9rem;
          transition: color 0.3s cubic-bezier(0.22, 1, 0.36, 1);
        }

        .footer-nav li a:hover {
          color: #C72631;
        }

        .footer-contact-item {
          display: flex;
          align-items: flex-start;
          gap: 0.75rem;
          margin-bottom: 1.1rem;
          color: #bbb;
        }

        .footer-contact-item svg,
        .footer-contact-item .footer-web-icon {
          color: #C72631;
          flex-shrink: 0;
          margin-top: 2px;
          font-size: 0.9rem;
        }

        .footer-contact-item p {
          color: #bbb;
          font-size: 0.9rem;
          line-height: 1.6;
          margin: 0;
        }

        .footer-contact-item a {
          color: #bbb;
          text-decoration: none;
          transition: color 0.3s cubic-bezier(0.22, 1, 0.36, 1);
        }

        .footer-contact-item a:hover {
          color: #C72631;
        }

        .footer-bottom {
          border-top: 1px solid #1e1e1e;
          padding-top: 2rem;
          text-align: center;
        }

        .footer-bottom p {
          color: var(--text-muted-dark, #8a8a8a);
          font-size: 0.85rem;
          margin: 0;
        }

        @media (max-width: 1024px) {
          .footer-content {
            grid-template-columns: 1fr 1fr;
            gap: 3rem;
          }

          .footer-about {
            grid-column: 1 / -1;
          }
        }

        @media (max-width: 768px) {
          /* Every footer link becomes a full-height row: 18px of text is not a
             tap target. The list gap goes to zero because the padding separates
             the rows now. */
          .footer-nav {
            gap: 0;
          }

          .footer-nav li a,
          .footer-contact-item a {
            display: flex;
            align-items: center;
            min-height: 44px;
          }

          .footer {
            padding: 3.5rem 0 2rem;
          }

          .footer-content {
            grid-template-columns: 1fr;
            gap: 2.5rem;
          }

          .footer-about {
            grid-column: auto;
          }

          .container {
            padding: 0 1.5rem;
          }
        }

        @media (max-width: 480px) {
          .container {
            padding: 0 1rem;
          }
        }
      `}</style>
    </footer>
  );
};

export default Footer;
